
import { functions, firestore } from "@/config/firebase";
import { ShoppingCart } from "@/models/events/checkout/shopping-cart.model";
import CardPaymentModal from "@/components/payments/CardPaymentModal.vue";
import { defineComponent, ref, onMounted, computed, Ref, inject } from "vue";
import { currencyDecimalFilter } from "@/vue-core/filters";
import events from "@/store/events";
import router from "@/config/router";
import { generateQR } from "@/services/qr";
import { useRoute } from "vue-router";
import {
  CheckoutCharge,
  Subtotal,
} from "@/models/events/checkout/subtotal.model";
import i18n from "@/i18n";
import { loaderStart, loaderDone } from "@/services/Loader";
import { showMessage } from "@/services/MessageService";
import { useStore } from "vuex";
import { QRAuth } from "@/vue-core/models/entrance/qrauth.model";
import useBreakpoints from "@/services/Breakpoints";
export default defineComponent({
  components: {
    AppCardPaymentModal: CardPaymentModal,
  },
  setup() {
    const isQuotaAvailable = computed(
      () => store.getters["event/isQuotaAvailable"]
    );
    const stripeDialogVisible = ref(false);
    const couponInput: Ref<string | null> = ref("");
    const validCoupong = ref(false);
    const coupongIdApply = ref("");
    const shoppingCart = ref({} as ShoppingCart);
    const stripeAccountId = ref("");
    const stripeClientSecret = ref("");
    const ticketType = ref("");
    const revenueTicket = ref(0);
    const route = useRoute();
    const store = useStore();
    const { type } = useBreakpoints();
    const eventData = computed(() => {
      return store.getters["user/eventData"];
    });

    const participantData = computed(() => {
      return store.getters["user/participantData"];
    });
    const applyCoupon = async () => {
      loaderStart();
      const getCoupon = functions.httpsCallable("getStripeCoupon");
      const stripeCouponValidation = functions.httpsCallable(
        "stripeCouponValidation"
      );
      const response = await getCoupon({
        coupongId: couponInput.value,
        eventId: route.params.eventID,
      });
      if (response.data === "not-exist") {
        showMessage({
          title: i18n.global.t("coupon.noExist"),
          message: i18n.global.t("coupon.noExistDetail"),
          type: "warning",
        });
        validCoupong.value = false;
        loaderDone();
      } else if (response.data) {
        const validRedemptions = await stripeCouponValidation({
          coupongId: couponInput.value,
          eventId: route.params.eventID,
          coupongType: "register",
        });
        if (validRedemptions.data !== "valid") {
          showMessage({
            title: i18n.global.t("coupon.invalidCoupon"),
            message: i18n.global.t("coupon.invalidCouponDetail"),
            type: "warning",
          });
          validCoupong.value = false;
          loaderDone();
          return;
        }
        await events.updateTicketParticipant({
          eventID: participantData.value.eventID,
          ticketToDelete: participantData.value.ticketType,
          ticketToUpdate: participantData.value.ticketType,
          coupon: response.data,
        });
        validCoupong.value = true;
        coupongIdApply.value = response.data.id;
        await getShoppingCart();
        showMessage({
          title: i18n.global.t("coupon.validCoupon"),
          message: i18n.global.t("coupon.validCouponDetail"),
          type: "success",
        });
        loaderDone();
      } else {
        showMessage({
          title: i18n.global.t("coupon.errorCoupon"),
          message: i18n.global.t("coupon.errorCouponDetail"),
          type: "warning",
        });
        loaderDone();
      }
    };

    const ticketPriceIsZero = ref(false);

    const updateUserTicket = ({ eventID, uid, ticketType }) => {
      return store.dispatch("user/updateUserTicket", {
        eventID,
        uid,
        ticketType,
      });
    };

    const getShoppingCart = async () => {
      const getPaymentIntents = functions.httpsCallable("getPaymentIntents");
      const response = (
        await getPaymentIntents({
          eventId: eventData.value.id,
          productType: "register",
        })
      ).data as ShoppingCart;
      if (response === "value-0") {
        ticketPriceIsZero.value = true;
        shoppingCart.value = await getShoppingCartWhenPriceZero();
      } else {
        ticketPriceIsZero.value = false;
        shoppingCart.value = response;
      }
    };

    const getShoppingCartWhenPriceZero = async () => {
      const cartsList = await firestore("events")
        .doc(eventData.value.id)
        .collection("participants")
        .doc(participantData.value.uid)
        .collection("carts")
        .get();

      return cartsList.docs[0].data() as ShoppingCart;
    };

    const formatCurrency = (subtotal: Subtotal) => {
      revenueTicket.value = subtotal.amount;
      return currencyDecimalFilter(
        subtotal.amount,
        subtotal.currency,
        i18n.global.locale
      );
    };
    const displayPaymentForm = () => {
      stripeDialogVisible.value = true;
    };
    const preparePaymentForm = (charge: CheckoutCharge) => {
      if (ticketPriceIsZero.value) {
        displayPaymentForm();
        return;
      }
      if (
        charge &&
        charge.paymentIntentClientSecret &&
        charge.stripeAccountId
      ) {
        stripeAccountId.value = charge.stripeAccountId;
        stripeClientSecret.value = charge.paymentIntentClientSecret;
        stripeDialogVisible.value = true;
      }
    };
    const validaStatus = async () => {
      await store.dispatch("user/changeParticipantStatusPay", true);
    };
    const updatePaymentStatusWhenPriceIsZero = async () => {
      loaderStart();
      try {
        const cartsList = await firestore("events")
          .doc(eventData.value.id)
          .collection("participants")
          .doc(participantData.value.uid)
          .collection("carts")
          .get();

        const qrData: QRAuth = {
          uid: participantData.value.uid,
          eventID: eventData.value.id,
        };

        const generatedQRImageData = await generateQR(qrData);

        await firestore("events")
          .doc(eventData.value.id)
          .collection("participants")
          .doc(participantData.value.uid)
          .collection("carts")
          .doc(cartsList.docs[0].data().id)
          .update({ status: "paid" });

        validaStatus();

        await firestore("events")
          .doc(eventData.value.id)
          .collection("participants")
          .doc(participantData.value.uid)
          .update({ status: "paid", qrCode: generatedQRImageData });
        await store.dispatch("event/sendEmailToConfirmPayment");
        await store.dispatch("event/counterStatisticsTickets");
        await store.dispatch("event/counterStatisticsParticipant", {
          eventID: eventData.value.id,
          type: "participantPaid",
        });
        loaderDone();
        showMessage({
          title: i18n.global.t("register.paymentSuccess.title"),
          message: i18n.global.t("register.paymentSuccess.message"),
          type: "success",
        });
        stripeDialogVisible.value = false;
        router.push({
          name: "Home",
          params: { eventID: eventData.value.id },
        });
      } catch (error) {
        loaderDone();
        stripeDialogVisible.value = false;
        showMessage({
          title: i18n.global.t("register.paymentError.title"),
          message: i18n.global.t("register.paymentError.message"),
          type: "error",
        });
      }
    };

    const selectTicket = async (ticketToUpdate) => {
      loaderStart();
      await events.updateTicketParticipant({
        eventID: participantData.value.eventID,
        ticketToDelete: participantData.value.ticketType,
        ticketToUpdate: ticketToUpdate,
      });
      await updateUserTicket({
        eventID: participantData.value.eventID,
        uid: participantData.value.uid,
        ticketType: ticketToUpdate,
      });
      validCoupong.value = false;
      await getShoppingCart();
      loaderDone();
    };
    function filterSize() {
      if (type.value === "lg") return "30%";
      if (type.value === "md") return "40%";
      return "80%";
    }
    onMounted(async () => {
      const emitter: any = inject("emitter");
      emitter.on("resetCart", () => {
        stripeDialogVisible.value = false;
        selectTicket(participantData.value.ticketType);
      });
      loaderStart();
      if (participantData.value.status === "cancelled") {
        await store.dispatch(
          "user/updateTicketParticipant",
          participantData.value
        );
      }
      await store.dispatch("event/getQuotaEvent", route.params.eventID);
      if (ticketPriceIsZero.value) {
        try {
          shoppingCart.value = await getShoppingCartWhenPriceZero();
          loaderDone();
        } catch (error) {
          loaderDone();
        }
      } else {
        try {
          await getShoppingCart();
          loaderDone();
        } catch (error) {
          loaderDone();
        }
      }
      if (shoppingCart.value.items)
        ticketType.value = shoppingCart.value.items[0].product.id;
    });

    return {
      shoppingCart,
      stripeDialogVisible,
      stripeAccountId,
      stripeClientSecret,
      ticketType,
      eventData,
      participantData,
      ticketPriceIsZero,
      getShoppingCart,
      getShoppingCartWhenPriceZero,
      formatCurrency,
      preparePaymentForm,
      displayPaymentForm,
      selectTicket,
      updatePaymentStatusWhenPriceIsZero,
      couponInput,
      applyCoupon,
      validCoupong,
      isQuotaAvailable,
      revenueTicket,
      filterSize,
    };
  },
  beforeMount() {
    if (this.participantData) {
      this.ticketType = this.participantData.ticketType;
    }
  },
});
